import { toast } from "react-toastify";
import { isFastDraftDeployment } from "src/reusable/Functions";
import { presentNotification } from "./registry";
import type { UserNotification } from "./types";
import { tabInBackground } from "./visibility";

export { tabInBackground } from "./visibility";

/** User-settings keys (bootstrapped in mre/ops/settings/settings_values.py). */
export const NOTIFICATION_SETTING_KEYS = {
  toast: "notification_toast",
  sound: "notification_sound",
  desktop: "notification_desktop",
} as const;

export interface NotificationPrefs {
  toast: boolean;
  sound: boolean;
  desktop: boolean;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  toast: true,
  sound: false,
  desktop: false,
};

export function prefsFromSettings(
  settings: Record<string, unknown> | null | undefined,
): NotificationPrefs {
  const read = (key: string, fallback: boolean) =>
    typeof settings?.[key] === "boolean"
      ? (settings[key] as boolean)
      : fallback;
  return {
    toast: read(
      NOTIFICATION_SETTING_KEYS.toast,
      DEFAULT_NOTIFICATION_PREFS.toast,
    ),
    sound: read(
      NOTIFICATION_SETTING_KEYS.sound,
      DEFAULT_NOTIFICATION_PREFS.sound,
    ),
    desktop: read(
      NOTIFICATION_SETTING_KEYS.desktop,
      DEFAULT_NOTIFICATION_PREFS.desktop,
    ),
  };
}

let audioContext: AudioContext | null = null;

const audioCtx = (): AudioContext | null => {
  const Ctor = window.AudioContext;
  if (!Ctor) return null;
  try {
    audioContext ??= new Ctor();
    return audioContext;
  } catch {
    return null;
  }
};

/**
 * Browsers create AudioContexts suspended until a user gesture; call this
 * from a click/keypress handler so a later chime is audible.
 */
export function unlockNotificationAudio(): void {
  const ctx = audioCtx();
  if (ctx?.state !== "suspended") return;
  try {
    Promise.resolve(ctx.resume()).catch(() => {
      // Autoplay policy still says no; the next gesture will try again.
    });
  } catch {
    // Same, synchronously.
  }
}

/** Test seam: forget the cached AudioContext. */
export function _resetNotificationAudio(): void {
  audioContext = null;
}

/**
 * Two-tone chime synthesized in place — no audio asset to ship or cache.
 * Skipped while the context is suspended: scheduling into a frozen clock
 * would make the chime play on the user's next click, long after the
 * notification it belongs to.
 */
export function playNotificationSound(): void {
  const ctx = audioCtx();
  if (!ctx) return;
  try {
    if (ctx.state !== "running") {
      unlockNotificationAudio();
      return;
    }
    const play = (freq: number, at: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.2, at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.3);
    };
    const now = ctx.currentTime;
    play(880, now);
    play(1174.66, now + 0.12);
  } catch {
    // No output device: a silent notification is fine.
  }
}

export type DesktopSupport =
  | NotificationPermission
  | "unsupported"
  | "insecure";

/**
 * Whether OS notifications can work here. Plain-HTTP deployments (common
 * on-prem) keep `window.Notification` but never prompt and report
 * "denied", so they get their own state rather than a misleading
 * "blocked in your browser settings".
 */
export const desktopSupport = (): DesktopSupport => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (!window.isSecureContext) return "insecure";
  return Notification.permission;
};

const desktopUsable = () => {
  const s = desktopSupport();
  return s !== "unsupported" && s !== "insecure";
};

/**
 * Ask for OS-notification permission if the browser hasn't decided yet.
 * Call from a user gesture: Chrome quiets or ignores prompts that aren't.
 * Resolves to the resulting permission (or the current one).
 */
export async function ensureDesktopPermission(): Promise<DesktopSupport> {
  const current = desktopSupport();
  if (current !== "default") return current;
  try {
    return await Notification.requestPermission();
  } catch {
    return "default";
  }
}

/**
 * Exercise every enabled channel right now, ignoring focus rules. Wired to a
 * button so the click doubles as the gesture that unlocks audio and lets
 * the permission prompt show. Only prompts when desktop alerts are on: a
 * denial is effectively permanent, so it must not be triggered by someone
 * who just wanted to hear the chime.
 */
export async function testNotificationAlerts(
  prefs: NotificationPrefs,
): Promise<void> {
  if (!prefs.toast && !prefs.sound && !prefs.desktop) {
    toast(
      "No alerts are enabled — turn some on in your notification settings.",
      {
        position: "bottom-right",
        type: "warning",
        autoClose: 5000,
      },
    );
    return;
  }
  if (prefs.sound) unlockNotificationAudio();
  if (prefs.toast) {
    toast("Test notification — this is what an alert looks like.", {
      position: "bottom-right",
      type: "info",
      autoClose: 5000,
    });
  }
  if (prefs.sound) playNotificationSound();
  if (prefs.desktop) {
    // Wait for the prompt so the first click actually produces a test alert.
    const permission = await ensureDesktopPermission();
    if (permission === "granted") {
      showDesktopNotification(
        "Test notification",
        "Desktop notifications are working.",
        { tag: "nuclearn-test-alert" },
      );
    }
  }
}

/**
 * Content icon for OS notifications (the same artwork the PWA manifest
 * uses). Chrome still shows its own logo as the *source* icon unless the
 * app is installed as a PWA; this only controls the notification image.
 */
const desktopNotificationIcon = (): string =>
  isFastDraftDeployment()
    ? "/images/product-logos/768x768 square fast draft.png"
    : "/images/favicon/android-chrome-192x192.png";

export function showDesktopNotification(
  title: string,
  body: string | null,
  { tag, onClick }: { tag?: string; onClick?: () => void } = {},
): void {
  if (!desktopUsable() || Notification.permission !== "granted") return;
  try {
    // `tag` makes the OS collapse the same alert from several tabs.
    const note = new Notification(title, {
      body: body ?? undefined,
      tag,
      icon: desktopNotificationIcon(),
    });
    if (onClick) {
      note.onclick = () => {
        window.focus();
        onClick();
        note.close();
      };
    }
  } catch {
    // Android Chrome throws "Illegal constructor" (page-scoped Notification
    // isn't allowed; it needs a service worker); nothing to do.
  }
}

/**
 * Deliver one listed notification through every channel the user enabled.
 * Nothing fires when the user is already looking at the resource in a
 * focused tab — they watched it happen. Desktop alerts only fire while the
 * tab is in the background; in the foreground the toast already covers it.
 */
export function runNotificationEffects(
  n: UserNotification,
  prefs: NotificationPrefs,
  open: ((link: string) => void) | undefined,
  { onScreen = false }: { onScreen?: boolean } = {},
): void {
  if (onScreen && !tabInBackground()) return;
  const { title, message, link } = presentNotification(n);
  const openLink = link && open ? () => open(link) : undefined;

  if (prefs.toast) {
    // Same container App.jsx mounts for NucToast; called directly because
    // NucToast's typed props don't cover toastId/onClick.
    toast(message ? `${title} — ${message}` : title, {
      toastId: n.id,
      position: "bottom-right",
      type: n.status === "error" ? "error" : "info",
      autoClose: 8000,
      onClick: openLink,
    });
  }
  if (prefs.sound) playNotificationSound();
  if (prefs.desktop && tabInBackground()) {
    showDesktopNotification(title, message, { tag: n.id, onClick: openLink });
  }
}
